import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import { UserService } from '../../services/user.service';
import { User, UserDetailResponse, UserUpdateStatusRequest } from '../../models/user.model';
import { CommonDialogData } from 'src/app/components/common-dialog/models/common-dialog.model';
import { CommonDialogComponent } from 'src/app/components/common-dialog/components/common-dialog/common-dialogcomponent';
import { MatDialog } from '@angular/material/dialog';
import { UserAddEditComponent } from '../manage/addedituser/addedituser.component';
import { ActionCellRendererComponent } from './action-cell-renderer.component';
import { StatusCellRendererComponent } from './status-cell-renderer.component';
import { CustomToastrService } from 'src/app/services/toastr.service';
import { environment } from 'src/environments/environment';
import { BreadcrumbItem, BreadcrumbService } from 'src/app/shared/services/breadcrumb.service';
import { UserActionCellRendererComponent } from './user-action-cell-renderer.component';
import { UserProfileService, UserRole } from '../../services/user-profile.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
})
export class UsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('agGrid') agGrid!: AgGridAngular;

  public environment = environment;

  // Grid configuration
  rowData: User[] = [];
  columnDefs: ColDef[] = [];
  components: any;
  gridContext: any;
  breadcrumbItems: BreadcrumbItem[] = [];

  // Dynamically set columnDefs based on rowData keys
  setColumnDefsFromRowData(): void {
    // Create Action column as last column
    const actionColumn: ColDef = {
      headerName: 'Action',
      field: 'actions',
      cellRenderer: UserActionCellRendererComponent,
      width: 75,
      minWidth: 75,
      maxWidth: 75,
      pinned: 'right',
      lockPosition: true,
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: false,
      suppressMovable: true,
      headerTooltip: 'Actions for each user row'
    };

    if (this.rowData.length > 0) {
      // Columns to exclude from display
      const excludedColumns = new Set([
        'id', 'stateId', 'countryId', 'roleId', 'locations',
        'businessUnitName', 'firstName', 'middleName', 'lastName'
      ]);

      // Define preferred column order with custom header names
      const orderedColumns: { field: string; headerName?: string }[] = [
        { field: 'fullName', headerName: 'Name' },
        { field: 'phone', headerName: 'Phone' },
        { field: 'email', headerName: 'Email' },
        { field: 'roleName', headerName: 'System Role(s)' },
        { field: 'displayName', headerName: 'Title' }
      ];

      const orderedFieldNames = new Set(orderedColumns.map(c => c.field));

      // Get remaining columns (not excluded, not in ordered list)
      const remainingKeys = Object.keys(this.rowData[0])
        .filter(key => !excludedColumns.has(key) && !orderedFieldNames.has(key));

      // Build full column list: ordered first, then remaining
      const allColumnKeys: { field: string; headerName?: string }[] = [
        ...orderedColumns,
        ...remainingKeys.map(key => ({ field: key }))
      ];

      const dataColumns = allColumnKeys
        .map(col => {
          const key = col.field;

          // For 'fullName', create a virtual column combining firstName + lastName
          if (key === 'fullName') {
            return {
              headerName: col.headerName || 'Name',
              field: 'fullName',
              sortable: true,
              resizable: true,
              flex: 1,
              minWidth: 250,
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              valueGetter: (params: any) => {
                const first = params.data?.firstName || '';
                const last = params.data?.lastName || '';
                return `${first} ${last}`.trim();
              },
              cellRenderer: (params: any) => {
                const value = params.value ?? '';
                const userId = params.data?.id || '';
                return `<span title="${value}">${value}</span>
                  <i class="fas fa-info-circle text-muted ms-1 user-info-icon"
                     style="cursor:pointer; font-size:12px;"
                     title="Click here to show assigned locations information"
                     data-user-id="${userId}"></i>`;
              }
            };
          }

          // For 'email', render a copy button before the address so users can
          // copy the email directly from the grid.
          if (key === 'email') {
            return {
              headerName: col.headerName || 'Email',
              field: 'email',
              sortable: true,
              resizable: true,
              flex: 1,
              headerClass: 'email',
              minWidth: 200,
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              cellRenderer: (params: any) => {
                const value = params.value ?? '';
                if (!value) {
                  return '';
                }
                const safeValue = String(value).replace(/"/g, '&quot;');
                // Copy icon (gray, no background) with the email text truncated
                // via ellipsis when it exceeds the column width.
                return `<span class="email-cell" style="display:flex; align-items:center; width:100%; min-width:0;">
                  <i class="fas fa-copy email-copy-icon"
                     style="cursor:pointer; font-size:15px; margin-right:8px; flex:0 0 auto;
                            color:#6c757d;"
                     title="Copy email address"
                     data-email="${safeValue}"></i>
                  <span title="${safeValue}"
                     style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0;">${value}</span>
                </span>`;
              }
            };
          }

          const sampleValue = this.rowData[0][key as keyof typeof this.rowData[0]];
          let filterType = 'agTextColumnFilter';

          if (typeof sampleValue === 'number') {
            filterType = 'agNumberColumnFilter';
          } else if (typeof sampleValue === 'string' && key.toLowerCase().includes('date')) {
            filterType = 'agDateColumnFilter';
          }

          return {
            headerName: col.headerName || (key === 'isActive' ? 'Status' : this.toTitleCase(key)),
            field: key,
            sortable: true,
            resizable: true,
            flex: 1,
            headerClass: key,
            minWidth: 200,
            filter: key === 'isActive' ? false : filterType,
            floatingFilter: !(key === 'isActive'),
            ...(typeof sampleValue === 'boolean' && {
              cellRenderer: StatusCellRendererComponent,
              filterValueGetter: (params: any) => {
                return params.data[key] ? 'Active' : 'Inactive';
              }
            }),
            ...(typeof sampleValue === 'string' && key.toLowerCase().includes('date')) && {
              cellRenderer: (params: any) => {
                if (params.value) {
                  const date = new Date(params.value);
                  return `<span title="${params.value}">${date.toLocaleDateString()}</span>`;
                }
                return '';
              }
            },
            ...(typeof sampleValue !== 'boolean' && !key.toLowerCase().includes('date') && {
              cellRenderer: (params: any) => {
                const value = params.value ?? '';
                return `<span title="${value}">${value}</span>`;
              }
            })
          };
        });

      // Append action column to data columns
      this.columnDefs = [...dataColumns, actionColumn];
    }
  }

  // Helper to convert camelCase or snake_case to Title Case
  private toTitleCase(str: string): string {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (txt) => txt.toUpperCase())
      .trim();
  }

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    flex: 1,
    filter: true,
    floatingFilter: true,
    filterParams: {
      buttons: ['reset']
    }
  };

  gridApi!: GridApi;
  // suppressDragLeaveHidesColumns keeps a column visible when its header is
  // dragged outside the grid, instead of removing it from the list.
  gridOptions: GridOptions = {
    suppressDragLeaveHidesColumns: true
  };

  // Data properties
  totalUsers = 0;
  pageSize = 10;
  currentPage = 0;

  // Delete confirmation properties
  userToDelete: any = null;

  // Status toggle confirmation properties
  userToToggleStatus: any = null;

  // Current editing user
  currentEditingUser: any = null;
  userEditData: any = null;
  isEditMode = false;

  // Global search properties
  globalSearchTerm: string = '';
  searchTimeout: any;
  filteredRowData: User[] = [];
  allRowData: User[] = [];

  // Filter properties
  isFilterVisible: boolean = false;
  roles: any[] = [];
  selectedRoles: string[] = [];
  selectedStatus: string = '';
  private previousRolesHadAll: boolean = false;

  // Assigned info popup
  showAssignedInfo: boolean = false;
  assignedInfoUser: any = null;
  assignedInfoPosition: { top: number; left: number } = { top: 0, left: 0 };
  assignedInfoOpenAbove: boolean = false;

  constructor(
    private userService: UserService,
    private router: Router,
    private dialog: MatDialog,
    private customToastrService: CustomToastrService,
    private breadcrumbService: BreadcrumbService,
    private userProfileService: UserProfileService
  ) {
    this.components = {
      actionCellRenderer: ActionCellRendererComponent,
      statusCellRenderer: StatusCellRendererComponent
    };
    this.gridContext = {
      editUser: this.editUser.bind(this),
      deleteUser: this.deleteUser.bind(this),
      toggleStatus: this.toggleStatus.bind(this),
    };
  }

  ngOnInit(): void {
    this.loadRoles();
  }

  ngAfterViewInit(): void {
    // Any additional initialization after view init
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // Load users data
  loadUsers(): void {
    // Filter out 'all' marker, when no specific roles selected pass all role names
    const filteredRoles = this.selectedRoles.filter(r => r !== 'all');
    const roleNames = filteredRoles.length > 0 ? filteredRoles : this.roles.map(r => r.name);
    const status = this.selectedStatus || 'all';

    this.userService.getUsers(roleNames, status).subscribe({
      next: (data) => {
        this.rowData = data;
        this.allRowData = [...data];
        this.filteredRowData = [...data];
        this.totalUsers = data.length;
        this.setColumnDefsFromRowData();
        this.updateBreadcrumb();
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.customToastrService.error('Failed to load users');
      }
    });
  }

  // Role hierarchy - same as addedituser component
  private roleHierarchy: { [key: string]: string[] } = {
    [UserRole.SUPER_ADMIN]: [UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.SUPER_ADMIN, UserRole.USER],
    [UserRole.CLIENT_ADMIN]: [UserRole.CLIENT_ADMIN, UserRole.CUSTOMER_ADMIN, UserRole.INSTALLER, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.USER],
    [UserRole.CUSTOMER_ADMIN]: [UserRole.CUSTOMER_ADMIN, UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER, UserRole.USER],
    [UserRole.RETAIL_ADMIN]: [UserRole.RETAIL_ADMIN, UserRole.RETAIL_MANAGER, UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER],
    [UserRole.RETAIL_MANAGER]: [UserRole.RETAIL_USER_WITH_CONTROL, UserRole.RETAIL_USER],
    [UserRole.RETAIL_USER_WITH_CONTROL]: [UserRole.RETAIL_USER],
    [UserRole.RETAIL_USER]: [],
    [UserRole.INSTALLER]: [],
    [UserRole.USER]: []
  };

  // Load roles for filter dropdown (filtered by current user's role hierarchy)
  loadRoles(): void {
    this.userService.getRoles().subscribe({
      next: (roles) => {
        // Filter roles based on current user's role hierarchy
        const currentUserRole = this.getCurrentUserRole();
        const allowedRoleNames = this.roleHierarchy[currentUserRole] || [];

        if (allowedRoleNames.length > 0) {
          this.roles = roles.filter((role: any) => allowedRoleNames.includes(role.name));
        } else {
          this.roles = roles;
        }

        // Select all filtered roles by default
        this.selectedRoles = ['all', ...this.roles.map(r => r.name)];
        this.previousRolesHadAll = true;
        // Load users after roles are available
        this.loadUsers();
      },
      error: (error) => {
        console.error('Error loading roles:', error);
        this.loadUsers();
      }
    });
  }

  // Get current user's role
  private getCurrentUserRole(): string {
    try {
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const parsed = JSON.parse(userProfile);
        return parsed.role || parsed.roleName || parsed.systemRole || UserRole.USER;
      }
    } catch (error) {
      console.warn('Error getting current user role:', error);
    }
    return UserRole.USER;
  }

  // Apply filters
  applyFilters(): void {
    this.loadUsers();
  }

  // Toggle select all roles - no longer needed, handled in onRoleSelectionChange
  toggleSelectAllRoles(): void { }

  // Handle role selection change - manages "All System Roles" logic
  onRoleSelectionChange(event: any): void {
    const allRoleNames = this.roles.map(r => r.name);
    const currentSelection: string[] = event.value;
    const hasAllNow = currentSelection.includes('all');

    if (hasAllNow && !this.previousRolesHadAll) {
      // User just clicked "All System Roles" to select → select all
      this.selectedRoles = ['all', ...allRoleNames];
    } else if (!hasAllNow && this.previousRolesHadAll) {
      // User just unchecked "All System Roles" → deselect all
      this.selectedRoles = [];
    } else {
      // Individual role change
      const selectedWithoutAll = currentSelection.filter(r => r !== 'all');
      if (selectedWithoutAll.length === allRoleNames.length) {
        // All individual roles now selected → auto-check "All"
        this.selectedRoles = ['all', ...allRoleNames];
      } else {
        // Not all selected → ensure "All" is unchecked
        this.selectedRoles = selectedWithoutAll;
      }
    }

    // Track state for next change
    this.previousRolesHadAll = this.selectedRoles.includes('all');
  }

  // Clear filters - remove all selections and call API
  clearFilters(): void {
    this.selectedRoles = [];
    this.previousRolesHadAll = false;
    this.selectedStatus = '';
    this.loadUsers();
  }

  // Grid ready event
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridOptions = params;

    // Listen for info icon clicks
    setTimeout(() => {
      const gridContainer = document.querySelector('.ag-body-viewport');
      if (gridContainer) {
        gridContainer.addEventListener('click', (event: Event) => {
          const target = event.target as HTMLElement;
          if (target.classList.contains('email-copy-icon')) {
            const email = target.getAttribute('data-email');
            if (email) {
              this.copyEmailToClipboard(email);
            }
            return;
          }
          if (target.classList.contains('user-info-icon')) {
            const userId = target.getAttribute('data-user-id');
            if (userId) {
              const rect = target.getBoundingClientRect();
              const viewportHeight = window.innerHeight;

              // Always position popup so it appears near the icon
              // If icon is in bottom half of screen, show above (bottom of popup at icon top)
              // If icon is in top half, show below
              let top: number;
              if (rect.top > viewportHeight / 2) {
                // Icon is in bottom half → open above, use 'auto' bottom approach
                // We'll set a negative offset from icon top
                top = rect.top - 10; // Will be used with 'bottom' alignment via transform
                this.assignedInfoPosition = { top: top, left: rect.left };
                this.showUserAssignedInfo(userId, true); // true = open above
              } else {
                top = rect.bottom + 5;
                this.assignedInfoPosition = { top: top, left: rect.left };
                this.showUserAssignedInfo(userId, false);
              }
            }
          }
        });
      }
    }, 100);
  }

  // Add new user
  addUser(): void {
    const dialogRef = this.dialog.open(UserAddEditComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: 'auto',
      maxHeight: '90vh',
      disableClose: true,
      data: { isEditMode: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  // Edit user
  editUser(user: User): void {
    this.userService.getUserById(user.id).subscribe({
      next: (userDetailResponse: UserDetailResponse) => {
        const dialogRef = this.dialog.open(UserAddEditComponent, {
          width: '90%',
          maxWidth: '1200px',
          height: 'auto',
          maxHeight: '90vh',
          disableClose: true,
          data: {
            isEditMode: true,
            userData: userDetailResponse  // Pass complete response with hierarchicalData
          }
        });
        // dialogRef.afterClosed().subscribe(result => {
        //   if (result) {
        //     this.loadUsers();
        //   }
        // });
      },
      error: (error) => {
        console.error('Error loading user details:', error);
        this.customToastrService.error('Failed to load user details');
      }
    });
  }

  // Delete user
  deleteUser(user: User): void {
    const dialogData: CommonDialogData = {
      title: 'Confirm Delete',
      message: `Are you sure you want to delete the user "${user.firstName} ${user.lastName}"?`,
      type: 'confirm',
      confirmText: 'Yes',
      cancelText: 'No',
      showCancel: true
    };

    const dialogRef = this.dialog.open(CommonDialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.userService.deleteUser(user.id).subscribe({
          next: () => {
            this.userToDelete = null;
            this.customToastrService.success('User deleted successfully!', 'Success');
            this.loadUsers();
          },
          error: (error) => {
            console.error('Error deleting user:', error);
            this.userToDelete = null;
            this.customToastrService.error(error.error.Message ?? 'Failed to delete user', 'Error');
          }
        });
      } else {
        this.userToDelete = null;
      }
    });
  }

  // Toggle user status
  toggleStatus(user: User): void {
    // Check if user is trying to change their own status
    const currentUserId = this.getCurrentLoggedInUserId();
    if (currentUserId && user.id === currentUserId) {
      this.customToastrService.warning('You cannot change your own status', 'Warning');
      return;
    }

    const newStatus = !user.isActive;
    const action = newStatus ? 'activate' : 'deactivate';

    const dialogData: CommonDialogData = {
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      message: `Are you sure you want to ${action} the user "${user.firstName} ${user.lastName}"?`,
      type: 'confirm',
      confirmText: 'Yes',
      cancelText: 'No',
      showCancel: true,
    };

    const dialogRef = this.dialog.open(CommonDialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        const statusRequest: UserUpdateStatusRequest = {
          isActive: newStatus
        };
        this.userService.updateUserStatus(user.id, statusRequest).subscribe({
          next: () => {
            this.customToastrService.success(`User ${action}d successfully`);
            this.loadUsers();
          },
          error: (error) => {
            console.error(`Error ${action}ing user:`, error);
            this.customToastrService.error(`Failed to ${action} user`);
          }
        });
      } else {
        this.userToToggleStatus = null;
      }
    });
  }

  // Get current logged-in user ID
  private getCurrentLoggedInUserId(): string | null {
    try {
      const userProfileData = localStorage.getItem('userProfile');
      if (userProfileData) {
        const userProfile = JSON.parse(userProfileData);
        return userProfile.id || null;
      }
    } catch (error) {
      console.error('Error getting logged-in user ID:', error);
    }
    return null;
  }

  // Global search functionality
  onSearchChange(searchTerm: string): void {
    this.globalSearchTerm = searchTerm;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.applyGlobalFilter();
    }, 300);
  }

  private applyGlobalFilter(): void {
    if (!this.globalSearchTerm.trim()) {
      this.filteredRowData = [...this.allRowData];
      this.rowData = this.filteredRowData;
      return;
    }

    const searchTerm = this.globalSearchTerm.toLowerCase();
    this.filteredRowData = this.allRowData.filter(user => {
      return Object.values(user).some(value =>
        value?.toString().toLowerCase().includes(searchTerm)
      );
    });

    this.rowData = this.filteredRowData;
  }

  // Clear search
  clearSearch(): void {
    this.globalSearchTerm = '';
    this.filteredRowData = [...this.allRowData];
    this.rowData = this.filteredRowData;
  }

  onClearAllFilters() {
    this.gridApi.setFilterModel(null);
    this.clearFilters();
  }

  // Export to CSV (optional functionality)
  exportToCsv(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: 'users.csv'
      });
    }
  }

  updateBreadcrumb(): void {
    this.breadcrumbItems = this.breadcrumbService.buildBreadcrumb('users', {
      totalUsers: this.totalUsers,
    });
  }

  // Copy an email address to the clipboard from the grid's copy icon.
  private copyEmailToClipboard(email: string): void {
    const onSuccess = () => this.customToastrService.success('Email copied to clipboard', 'Copied');
    const onFailure = () => this.customToastrService.error('Failed to copy email', 'Error');

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(email).then(onSuccess).catch(onFailure);
      return;
    }

    // Fallback for non-secure contexts (e.g. plain http) where the
    // Clipboard API is unavailable.
    try {
      const textarea = document.createElement('textarea');
      textarea.value = email;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? onSuccess() : onFailure();
    } catch {
      onFailure();
    }
  }

  // Show assigned information popup
  showUserAssignedInfo(userId: string, openAbove: boolean = false): void {
    const user = this.allRowData.find((u: any) => u.id === userId);
    if (user) {
      this.assignedInfoUser = user;
      this.assignedInfoOpenAbove = openAbove;
      this.showAssignedInfo = true;
    }
  }

  // Close assigned information popup
  closeAssignedInfo(): void {
    this.showAssignedInfo = false;
    this.assignedInfoUser = null;
  }

  // Get unique customer names from user data (businessUnitName field - array of strings)
  getUniqueCustomers(locations: any[]): string[] {
    const user = this.assignedInfoUser;
    if (!user?.businessUnitName) return [];
    // businessUnitName is an array of strings
    if (Array.isArray(user.businessUnitName)) {
      return Array.from(new Set(user.businessUnitName.filter((n: string) => n && n.trim())));
    }
    // Fallback: if it's a comma-separated string
    const names: string[] = user.businessUnitName.split(',').map((n: string) => n.trim()).filter((n: string) => n);
    return Array.from(new Set(names));
  }

  // Get unique location names from locations array (array of strings)
  getUniqueLocations(locations: any[]): string[] {
    if (!locations || !Array.isArray(locations)) return [];
    // locations is an array of strings
    const names: string[] = locations.filter((loc: any) => loc && (typeof loc === 'string' ? loc.trim() : false));
    return Array.from(new Set(names));
  }
}